package com.trevorwiebe.timesheet.punch.presentation.composables

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.expandVertically
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.shrinkVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Alignment.Companion.CenterVertically
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.intl.Locale
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.toUpperCase
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.core.domain.Util.toFriendlyDate
import com.trevorwiebe.timesheet.core.domain.Util.toFriendlyDayOfWeek
import com.trevorwiebe.timesheet.core.domain.model.Punch
import com.trevorwiebe.timesheet.core.domain.model.Rate
import com.trevorwiebe.timesheet.core.presentation.common.DestructiveButton
import com.trevorwiebe.timesheet.core.presentation.common.RateSelector
import com.trevorwiebe.timesheet.core.presentation.common.TimeSheetButton
import com.trevorwiebe.timesheet.punch.presentation.uiUtils.UiPunch
import kotlinx.datetime.LocalDateTime
import org.jetbrains.compose.resources.painterResource
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.error_circle
import timesheet.composeapp.generated.resources.holiday
import timesheet.composeapp.generated.resources.pto

@Composable
fun PunchItem(
    editable: Boolean,
    date: LocalDateTime,
    holiday: String?,
    hoursWorked: List<Pair<String, Double>>,
    punches: List<UiPunch>,
    holidayAndPtoPunches: List<UiPunch>,
    onShowConfirmDelete: (punchUiModel: UiPunch) -> Unit,
    onShowAddHours: () -> Unit,
    onUpdateRate: (UiPunch) -> Unit,
    onTimeSelected: (Punch) -> Unit,
    onAddPTO: () -> Unit,
    rateList: List<Rate>,
) {
    var editing by remember { mutableStateOf(false) }
    val elevation = if (editing) 8.dp else 0.dp
    val ptoId = remember {
        Util.getRateIDFromName("PTO", rateList)
    }
    Card(
        shape = RoundedCornerShape(8.dp),
        modifier = if (editable) {
            Modifier
                .fillMaxWidth()
                .padding(8.dp)
                .clip(RoundedCornerShape(8.dp))
                .clickable { editing = !editing }
        } else {
            Modifier
                .fillMaxWidth()
                .padding(8.dp)
        },
        elevation = CardDefaults.cardElevation(
            defaultElevation = elevation
        ),
        colors = CardDefaults.cardColors(
            containerColor = if (editing) {
                MaterialTheme.colorScheme.surface
            }else{
                MaterialTheme.colorScheme.background
            }
        )
    ) {
        Column(
            modifier = Modifier.padding(8.dp)
        ) {
            PunchHeader(date = date, holiday = holiday, editing = editing)
            PunchBody(
                editing = editing,
                punches = punches,
                holidayAndPtoPunches = holidayAndPtoPunches,
                onShowConfirmDelete = onShowConfirmDelete,
                onTimeSelected = onTimeSelected,
                rateList = rateList,
                onUpdateRate = onUpdateRate,
                hoursWorked = hoursWorked
            )
            ConfirmChangesRow(
                onShowAddHours = onShowAddHours,
                onAddPTO = onAddPTO,
                isEditing = editing,
                addPTOEnabled = holidayAndPtoPunches
                    .none { it.punchIn.rateId == ptoId }
            )
        }
    }
}

@Composable
private fun PunchHeader(
    date: LocalDateTime,
    holiday: String? = null,
    editing: Boolean
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = CenterVertically
    ) {
        Text(
            text = toFriendlyDayOfWeek(date.date),
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
            color = if(editing) MaterialTheme.colorScheme.onBackground
                else MaterialTheme.colorScheme.secondary
        )
        if (holiday != null) {
            Text(
                modifier = Modifier.padding(start = 16.dp),
                text = holiday.toUpperCase(Locale.current),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.secondary
            )
        }
        Spacer(modifier = Modifier.weight(1f))
        Text(
            text = toFriendlyDate(date.date),
            fontWeight = FontWeight.Bold,
            fontSize = 18.sp,
            color = if(editing) MaterialTheme.colorScheme.onBackground
                else MaterialTheme.colorScheme.secondary
        )
    }
}

@Composable
private fun PunchBody(
    editing: Boolean,
    punches: List<UiPunch>,
    holidayAndPtoPunches: List<UiPunch>,
    onShowConfirmDelete: (punchUiModel: UiPunch) -> Unit,
    onTimeSelected: (Punch) -> Unit,
    onUpdateRate: (UiPunch) -> Unit,
    rateList: List<Rate>,
    hoursWorked: List<Pair<String, Double>>
) {

    val ptoId = rateList.firstOrNull { it.description == "PTO" }?.id
    val holidayId = rateList.firstOrNull { it.description == "Holiday" }?.id

    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        if (punches.isEmpty() && holidayAndPtoPunches.isEmpty()) {
            Text(
                modifier = Modifier.padding(bottom = 8.dp),
                text = "No clock times for this day",
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.secondary
            )
        } else {
            Spacer(modifier = Modifier.height(8.dp))
            holidayAndPtoPunches.forEach { uiPunch ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = CenterVertically
                ) {
                    Row(
                        modifier = Modifier.weight(1f),
                        verticalAlignment = CenterVertically
                    ) {
                        Box(
                            modifier = Modifier
                                .clip(CircleShape)
                                .background(MaterialTheme.colorScheme.tertiary)
                                .padding(6.dp)
                        ){
                            Icon(
                                painter = painterResource(
                                    if(uiPunch.punchIn.rateId == ptoId) Res.drawable.pto
                                    else Res.drawable.holiday
                                ),
                                contentDescription = "holiday_pto",
                                tint = MaterialTheme.colorScheme.onTertiary,
                                modifier = Modifier
                                    .size(18.dp)
                            )
                        }
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            modifier = Modifier.weight(1f),
                            text = if(uiPunch.punchIn.rateId == ptoId) "PTO time used"
                                else if (uiPunch.punchIn.rateId == holidayId) "Holiday pay applied"
                                else "",
                            fontWeight = FontWeight.Bold
                        )
                    }
                    Spacer(modifier = Modifier.width(16.dp))
                    AnimatedVisibility(
                        visible = editing && uiPunch.punchIn.rateId == ptoId,
                        modifier = Modifier.weight(1f),
                    ){
                        DestructiveButton(
                            modifier = Modifier.fillMaxWidth().height(35.dp),
                            text = "Delete Time",
                            onClick = { onShowConfirmDelete(uiPunch) }
                        )
                    }
                }
                Spacer(modifier = Modifier.height(2.dp))
            }
            punches.forEachIndexed { index, uiPunch ->

                if(index != 0) {
                    Spacer(modifier = Modifier.height(8.dp))
                }
                Column(
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth()
                    ) {

                        Column(
                            modifier = Modifier.weight(1f)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = CenterVertically,
                            ) {
                                if (!uiPunch.punchInError.isNullOrEmpty()) {
                                    Icon(
                                        modifier = Modifier.size(24.dp),
                                        painter = painterResource(Res.drawable.error_circle),
                                        contentDescription = "cancel",
                                        tint = MaterialTheme.colorScheme.error
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                }
                                Text("In: ", fontWeight = FontWeight.Bold)
                                Spacer(modifier = Modifier.weight(1f))
                                StyleTransitionTextField(
                                    uiPunch.punchIn,
                                    editing,
                                    onTimeSelected,
                                    uiPunch.punchInError != null
                                )
                            }
                            AnimatedVisibility(visible = editing && uiPunch.punchInError != null) {
                                Text(
                                    fontSize = 14.sp,
                                    text = uiPunch.punchInError ?: "",
                                    color = MaterialTheme.colorScheme.error
                                )
                            }
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = CenterVertically,
                            ) {
                                if (!uiPunch.punchOutError.isNullOrEmpty()) {
                                    Icon(
                                        modifier = Modifier.size(24.dp),
                                        painter = painterResource(Res.drawable.error_circle),
                                        contentDescription = "cancel",
                                        tint = MaterialTheme.colorScheme.error
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                }
                                Text("Out: ", fontWeight = FontWeight.Bold)
                                Spacer(modifier = Modifier.weight(1f))
                                StyleTransitionTextField(
                                    uiPunch.punchOut,
                                    editing,
                                    onTimeSelected,
                                    uiPunch.punchOutError != null
                                )
                            }
                            AnimatedVisibility(visible = editing && uiPunch.punchOutError != null) {
                                Text(
                                    fontSize = 14.sp,
                                    text = uiPunch.punchOutError ?: "",
                                    color = MaterialTheme.colorScheme.error
                                )
                            }
                        }
                        Spacer(modifier = Modifier.width(8.dp))
                        Column(
                            modifier = Modifier.weight(1f)
                        ) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = CenterVertically,
                            ) {
                                Text(
                                    text = "Rate:",
                                    fontWeight = FontWeight.Bold
                                )
                                Spacer(modifier = Modifier.weight(1f))
                                if (editing) {
                                    RateSelector(
                                        modifier = Modifier
                                            .padding(vertical = 4.dp)
                                            .width(110.dp)
                                            .height(33.dp),
                                        rateList = rateList.filter { it.userFacing },
                                        selectedRate = uiPunch.getRate(rateList),
                                        onRateSelected = {
                                            val newUiPunch = uiPunch.copy(
                                                punchIn = uiPunch.punchIn.copy(rateId = it.id),
                                                punchOut = uiPunch.punchOut?.copy(rateId = it.id)
                                            )
                                            onUpdateRate(newUiPunch)
                                        }
                                    )
                                } else {
                                    Text(
                                        modifier = Modifier
                                            .background(MaterialTheme.colorScheme.background)
                                            .width(110.dp)
                                            .padding(
                                                start = 8.dp,
                                                end = 8.dp,
                                                top = 5.dp,
                                                bottom = 5.dp
                                            ),
                                        text = uiPunch.getRate(rateList)?.description
                                            ?: "unavailable",
                                        textAlign = TextAlign.Center
                                    )
                                }
                            }
                            AnimatedVisibility(
                                visible = editing,
                                enter = fadeIn() + expandVertically(),
                                exit = fadeOut() + shrinkVertically(),
                            ) {
                                Column(
                                    modifier = Modifier.fillMaxWidth(),
                                    horizontalAlignment = Alignment.End
                                ) {
                                    Spacer(modifier = Modifier.height(6.dp))
                                    DestructiveButton(
                                        modifier = Modifier.fillMaxWidth().height(35.dp),
                                        text = "Delete Time",
                                        onClick = { onShowConfirmDelete(uiPunch) }
                                    )
                                }
                            }
                        }
                    }
                }
            }
            Row(
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    modifier = Modifier.padding(bottom = 2.dp),
                    text = buildAnnotatedString {
                        hoursWorked.forEachIndexed { index, (label, value) ->
                            withStyle(style = androidx.compose.ui.text.SpanStyle(
                                fontWeight = FontWeight.Bold
                            )) {
                                append(label)
                            }
                            append(": $value")
                            if (index != hoursWorked.lastIndex) {
                                append(" - ")
                            }
                        }
                    },
                    fontSize = 14.sp,
                    color = MaterialTheme.colorScheme.onBackground
                )
            }
        }
    }
}

@Composable
fun StyleTransitionTextField(
    punch: Punch?,
    isEditing: Boolean,
    onTimeSelected: (Punch) -> Unit,
    error: Boolean,
) {

    Box(
        modifier = Modifier.padding(4.dp),
        contentAlignment = Alignment.Center
    ) {
        AnimatedContent(targetState = isEditing, label = "EditableTextField") { editing ->

            val background = if (error) MaterialTheme.colorScheme.errorContainer else MaterialTheme.colorScheme.background
            val time = remember(punch) { Util.toFriendlyTime(punch?.dateTime) }
            val textColor = if (error) MaterialTheme.colorScheme.onErrorContainer else MaterialTheme.colorScheme.onBackground

            if (editing && punch != null) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .background(background)
                        .clickable { onTimeSelected(punch) }
                ) {
                    Text(
                        modifier = Modifier
                            .width(110.dp)
                            .padding(top = 6.dp, bottom = 6.dp, start = 8.dp, end = 8.dp),
                        text = time,
                        textAlign = TextAlign.Center,
                        color = textColor
                    )
                }
            } else {
                Text(
                    modifier = Modifier.width(110.dp).padding(0.dp),
                    text = time,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}

@Composable
fun ConfirmChangesRow(
    onShowAddHours: () -> Unit,
    onAddPTO: () -> Unit,
    isEditing: Boolean,
    addPTOEnabled: Boolean
) {
    AnimatedVisibility(
        visible = isEditing,
        enter = fadeIn() + expandVertically(),
        exit = fadeOut() + shrinkVertically(),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = CenterVertically
        ) {
            TimeSheetButton(
                modifier = Modifier.width(150.dp).height(50.dp),
                onClick = onAddPTO,
                text = "Add PTO",
                loading = false,
                enabled = addPTOEnabled
            )
            Spacer(modifier = Modifier.weight(1f))
            TimeSheetButton(
                modifier = Modifier.width(150.dp).height(50.dp),
                onClick = onShowAddHours,
                text = "Add Hours",
                loading = false,
                enabled = true
            )
        }
    }
}